import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';

export type ChangeRecord = {
  fieldName: string
  before: string
  after: string
}

export type ChangeHistoryAction = 'create' | 'update' | 'duplicate' | 'delete'

export type ChangeHistoryRow = {
  id: number
  created_at: string
  created_by: string
  company_id: number
  campaign_id: number | null
  llm_id: number | null
  tool_id: number | null
  type: string
  action: ChangeHistoryAction
  changes: ChangeRecord[]
  campaigns: { name: string } | null
  llms: { name: string; view_type: string } | null
  tools: { name: string } | null
}

type ChangeHistoryResponse = {
  success: boolean
  page_size: number
  page_id: number
  rows: ChangeHistoryRow[]
  total: number
}

// Hook for fetching change history
export function useChangeHistory(params: {
  page: number
  pageSize: number
  orderBy: string
  orderType: 'asc' | 'desc'
  action?: string
  type?: string
  startDate?: string
  endDate?: string
  createdBy?: string
}) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['change-history', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      searchParams.set('page_id', String(params.page))
      searchParams.set('page_size', String(params.pageSize))
      searchParams.set('order_by', params.orderBy)
      searchParams.set('order_type', params.orderType)
      if (params.action && params.action !== 'all') {
        searchParams.set('action', params.action)
      }
      if (params.type && params.type !== 'all') {
        searchParams.set('type', params.type)
      }
      if (params.startDate) {
        searchParams.set('start_date', params.startDate)
      }
      if (params.endDate) {
        searchParams.set('end_date', params.endDate)
      }
      if (params.createdBy && params.createdBy !== 'all') {
        searchParams.set('created_by', params.createdBy)
      }
      return apiClient<ChangeHistoryResponse>(
        `/change-history?${searchParams.toString()}`
      )
    },
  })
}